import { Link } from "react-router-dom";
import { Eye, Shield, Target, Lightbulb, ArrowRight, Check } from "lucide-react";
import { usePageMeta } from "@/hooks/usePageMeta";
import { JsonLd } from "@/components/JsonLd";

const values = [
  {
    icon: Eye,
    title: "Claritate",
    text: "Clientul trebuie să știe ce primește, ce este inclus și cine răspunde atunci când apare o problemă. Comunicarea bună nu este un extra, ci parte din serviciu.",
  },
  {
    icon: Shield,
    title: "Responsabilitate",
    text: "Mentenanța corectă nu înseamnă să aștepți până când ceva se strică. Înseamnă prevenție, monitorizare, verificări recurente și reacție clară atunci când este nevoie.",
  },
  {
    icon: Lightbulb,
    title: "Transparență",
    text: "Credem în accesuri clare, procese curate și relații sănătoase. Nu credem în dependențe artificiale, în zone gri sau în situații în care clientul nu știe cine controlează cu adevărat site-ul.",
  },
  {
    icon: Target,
    title: "Orientare spre business",
    text: "Nu ne uităm doar la partea tehnică. Ne uităm și la impactul real asupra stabilității, lead-urilor, comenzilor, vitezei de răspuns și continuității operaționale.",
  },
];

const steps = [
  { num: "01", title: "Audit tehnic inițial", text: "Analizăm starea actuală a site-ului, identificăm riscurile vizibile și înțelegem dacă setup-ul actual este potrivit pentru mentenanță, stabilitate și hosting." },
  { num: "02", title: "Onboarding și accesuri", text: "Preluăm accesurile necesare, verificăm punctele critice și clarificăm ce există deja la nivel de backup, securitate, infrastructură și administrare." },
  { num: "03", title: "Mentenanță și monitorizare", text: "Gestionăm actualizările, monitorizarea, verificările recurente și intervențiile necesare, astfel încât site-ul să rămână funcțional și controlat." },
  { num: "04", title: "Suport și continuitate", text: "Când apare o problemă sau o nevoie reală, răspundem clar și intervenim organizat, fără improvizații și fără ambiguități." },
];

const audience = [
  "Companii care folosesc WordPress pentru prezentare și lead generation",
  "Business-uri care nu au echipă tehnică internă",
  "Magazine online WooCommerce care au nevoie de mai multă atenție operațională",
  "Echipe care vor suport clar, stabilitate și mai puține zone gri",
];

export default function AboutPage() {
  usePageMeta(
    "Despre Ordell WebCare - Mentenanță WordPress Profesională",
    "Ordell WebCare oferă mentenanță și suport WordPress pentru companii care vor un site stabil, sigur și gestionat corect. Claritate, responsabilitate, transparență."
  );

  return (
    <>
      <JsonLd page="despre" />
      {/* Hero */}
      <section data-testid="about-hero" className="py-16 lg:py-24">
        <div className="section-container max-w-3xl">
          <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-none font-bold text-[#1A1D20] mb-6">
            Ordell WebCare există pentru business-uri care vor ordine, claritate și suport real.
          </h1>
          <p className="text-base sm:text-lg text-[#4A5568] leading-relaxed mb-4">
            Credem că un site WordPress important pentru business nu ar trebui administrat haotic, reactiv sau dependent de un singur om greu de găsit.
          </p>
          <p className="text-sm text-[#4A5568] leading-relaxed">
            Ordell WebCare este construit pentru companiile care vor mai mult decât intervenții ocazionale. Este pentru echipe care au nevoie de un partener tehnic serios, cu procese clare, răspunsuri directe și o abordare orientată spre stabilitate, continuitate și control.
          </p>
        </div>
      </section>

      {/* Cine suntem */}
      <section data-testid="about-who" className="py-16 lg:py-20 bg-[#F1F3F5]">
        <div className="section-container max-w-3xl">
          <h2 className="font-['Outfit'] text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight font-semibold text-[#1A1D20] mb-6">
            Un serviciu Ordell, susținut de experiența Maze Digital
          </h2>
          <p className="text-base sm:text-lg text-[#4A5568] leading-relaxed mb-4">
            Ordell WebCare este un serviciu Maze Digital, companie activă din 2017 în zona de programare IT și soluții digitale. Am creat acest serviciu pentru companiile care au nevoie de mai mult decât simple update-uri: au nevoie de mentenanță reală, suport responsabil și o infrastructură digitală tratată cu seriozitate.
          </p>
          <p className="text-base sm:text-lg text-[#4A5568] leading-relaxed">
            Experiența noastră tehnică ne ajută să privim site-urile WordPress și magazinele WooCommerce nu ca pe o simplă colecție de pluginuri, ci ca pe un activ de business care trebuie să rămână stabil, sigur și funcțional.
          </p>
        </div>
      </section>

      {/* Ce ne diferentiaza */}
      <section data-testid="about-diff" className="py-20 lg:py-28">
        <div className="section-container max-w-3xl">
          <h2 className="font-['Outfit'] text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight font-semibold text-[#1A1D20] mb-6">
            Nu tratăm site-ul ca pe un task uitat într-un inbox.
          </h2>
          <p className="text-base sm:text-lg text-[#4A5568] leading-relaxed mb-4">
            Pentru multe business-uri, site-ul este esențial pentru imagine, lead-uri, relația cu clienții sau vânzări. Cu toate acestea, de multe ori este întreținut doar atunci când apare o problemă: un update care strică ceva, un formular care nu mai trimite sau un magazin care începe să piardă comenzi.
          </p>
          <p className="text-base sm:text-lg text-[#4A5568] leading-relaxed">
            Noi credem că această abordare este prea riscantă. De aceea, Ordell WebCare este construit în jurul unei idei simple: mai puțin haos, mai puțină dependență de noroc și mai mult control asupra unui sistem important pentru business.
          </p>
        </div>
      </section>

      {/* In ce credem */}
      <section data-testid="about-values" className="py-20 lg:py-28 bg-[#F1F3F5]">
        <div className="section-container">
          <h2 className="font-['Outfit'] text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight font-semibold text-[#1A1D20] mb-4">
            Principiile după care lucrăm
          </h2>
          <div className="grid sm:grid-cols-2 gap-6 mt-10">
            {values.map((v, i) => (
              <div
                key={i}
                data-testid={`about-value-${i}`}
                className="bg-white rounded-2xl p-8 border border-[#0F2942]/10 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
              >
                <div className="w-10 h-10 rounded-xl bg-[#F0F5F4] flex items-center justify-center mb-4">
                  <v.icon className="w-5 h-5 text-[#1A4A40]" />
                </div>
                <h3 className="font-['Outfit'] text-xl font-medium text-[#1A1D20] mb-2">{v.title}</h3>
                <p className="text-sm text-[#4A5568] leading-relaxed">{v.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* De ce facem asta */}
      <section data-testid="about-why" className="py-20 lg:py-28">
        <div className="section-container max-w-3xl">
          <h2 className="font-['Outfit'] text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight font-semibold text-[#1A1D20] mb-6">
            Pentru că prea multe site-uri importante sunt tratate superficial.
          </h2>
          <p className="text-base sm:text-lg text-[#4A5568] leading-relaxed mb-4">
            Prea mulți antreprenori trăiesc cu senzația că site-ul lor este o cutie neagră. Nu știu dacă există backup real, nu știu dacă update-urile sunt făcute corect și nu știu cine își asumă intervenția atunci când apare o problemă.
          </p>
          <p className="text-base sm:text-lg text-[#4A5568] leading-relaxed mb-4">
            În multe cazuri, există dependență de o singură persoană, lipsă de documentație, lipsă de procese și mult prea multă improvizație. Iar când site-ul devine instabil, lent sau vulnerabil, costul nu este doar tehnic. Se pierde timp, se pierde încredere și, uneori, se pierd lead-uri sau vânzări.
          </p>
          <p className="text-sm font-medium text-[#0F2942]">
            Ordell WebCare a fost creat tocmai pentru a înlocui această incertitudine cu un proces clar, suport real și mai multă liniște operațională.
          </p>
        </div>
      </section>

      {/* Cum lucram */}
      <section data-testid="about-how" className="py-20 lg:py-28 bg-[#F1F3F5]">
        <div className="section-container">
          <div className="max-w-3xl mb-12">
            <h2 className="font-['Outfit'] text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight font-semibold text-[#1A1D20] mb-4">
              Un proces simplu, curat și ușor de urmărit
            </h2>
            <p className="text-base sm:text-lg text-[#4A5568] leading-relaxed">
              Combinăm experiența tehnică Maze Digital cu un mod de lucru simplu și organizat. Nu complicăm inutil lucrurile și nu ascundem procesul în spatele jargonului.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((s, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 border border-[#0F2942]/10">
                <span className="font-['Outfit'] text-3xl font-bold text-[#0F2942]/10 block mb-3">{s.num}</span>
                <h3 className="font-['Outfit'] text-lg font-medium text-[#1A1D20] mb-2">{s.title}</h3>
                <p className="text-sm text-[#4A5568] leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pentru cine */}
      <section data-testid="about-audience" className="py-20 lg:py-28">
        <div className="section-container max-w-3xl">
          <h2 className="font-['Outfit'] text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight font-semibold text-[#1A1D20] mb-4">
            Lucrăm cel mai bine cu business-uri care depind de site-ul lor.
          </h2>
          <div className="space-y-3 mt-8 mb-8">
            {audience.map((item, i) => (
              <div key={i} className="flex items-start gap-3 bg-[#F0F5F4]/50 rounded-xl p-4">
                <Check className="w-4 h-4 text-[#1A4A40] mt-0.5 shrink-0" />
                <span className="text-sm text-[#1A1D20]">{item}</span>
              </div>
            ))}
          </div>
          <p className="text-sm text-[#4A5568] leading-relaxed">
            Nu este un serviciu gândit pentru proiecte tratate superficial sau pentru situații în care mentenanța este văzută doar ca o cheltuială minimă de bifat. Este construit pentru companii care înțeleg că site-ul lor este parte din modul în care funcționează business-ul.
          </p>
        </div>
      </section>

      {/* Infrastructura */}
      <section data-testid="about-infra" className="py-20 lg:py-28 bg-[#0A1118] text-white">
        <div className="section-container max-w-3xl mx-auto">
          <h2 className="font-['Outfit'] text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight font-semibold mb-6">
            Când proiectul cere mai mult, și infrastructura trebuie să fie la nivelul potrivit.
          </h2>
          <p className="text-white/70 leading-relaxed mb-4">
            Pentru proiectele unde stabilitatea și continuitatea contează cu adevărat, nu ne limităm la soluții de hosting shared aglomerate și restrictive. În funcție de nevoi, folosim infrastructură managed sau dedicată, configurată pentru mai mult control, predictibilitate și o mai bună gestionare a traficului.
          </p>
          <p className="text-white/60 text-sm italic">
            Asta înseamnă un mediu mai sănătos pentru WordPress și WooCommerce, mai puține blocaje artificiale și o bază mai bună pentru performanță, securitate și funcționare stabilă.
          </p>
        </div>
      </section>

      {/* Mai mult decat mentenanta */}
      <section data-testid="about-more" className="py-20 lg:py-28">
        <div className="section-container max-w-3xl">
          <h2 className="font-['Outfit'] text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight font-semibold text-[#1A1D20] mb-6">
            Ordell WebCare înseamnă mai mult decât update-uri și backup-uri.
          </h2>
          <p className="text-base sm:text-lg text-[#4A5568] leading-relaxed mb-4">
            Înseamnă un mod mai matur de a gestiona un site WordPress important pentru business. Înseamnă procese clare, răspunsuri directe, mentenanță responsabilă și infrastructură potrivită atunci când proiectul are nevoie de mai mult.
          </p>
          <p className="text-sm font-medium text-[#0F2942]">
            Pe scurt, înseamnă să știi că site-ul tău este în grijă bună.
          </p>
        </div>
      </section>

      {/* CTA final */}
      <section data-testid="about-cta" className="py-20 lg:py-28 bg-[#F1F3F5]">
        <div className="section-container max-w-3xl mx-auto text-center">
          <p className="text-[#4A5568] leading-relaxed mb-8">
            Scopul auditului tehnic inițial este să îți spunem clar ce vedem, unde există riscuri și ce am recomanda mai departe. Simplu, onest și aplicat pe nevoile reale ale business-ului tău.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/contact"
              data-testid="about-cta-btn"
              className="inline-flex items-center justify-center gap-2 bg-[#0F2942] text-white font-medium px-8 py-3.5 rounded-xl hover:bg-[#0F2942]/90 transition-all"
            >
              Cere auditul tehnic inițial <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/servicii"
              data-testid="about-services-btn"
              className="inline-flex items-center justify-center gap-2 bg-white text-[#0F2942] border border-[#0F2942]/10 font-medium px-8 py-3.5 rounded-xl hover:bg-[#F0F5F4] transition-all"
            >
              Vezi serviciile
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
